import { ThemeProvider as MUIThemeProvider, createTheme } from "@mui/material";
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";

type ThemeMode = "light" | "dark";

type ThemeContextType = {
  mode: ThemeMode;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

declare module "@mui/material/styles" {
  interface Palette {
    MFT_ORANGE: "#FF8C3D";
    MFT_DARK_BLUE: "#093952";
    MFT_LIGHT_BLUE: "#BCE1FF";
    MFT_OFF_WHITE: "#F1F6F8";
    MFT_CREAM: "#DFD0BF";
    MFT_GREEN: "#8ACEBA";
    NICE_GREEN: "#66D575";
    NICE_ORANGE: "#FEC843";
  }
  interface PaletteOptions {
    MFT_ORANGE: "#FF8C3D";
    MFT_DARK_BLUE: "#093952";
    MFT_LIGHT_BLUE: "#BCE1FF";
    MFT_OFF_WHITE: "#F1F6F8";
    MFT_CREAM: "#DFD0BF";
    MFT_GREEN: "#8ACEBA";
    NICE_GREEN: "#66D575";
    NICE_ORANGE: "#FEC843";
  }
}

declare module "@mui/material/Typography" {
  export interface TypographyPropsColorOverrides {
    MFT_ORANGE: true;
    MFT_DARK_BLUE: true;
    MFT_LIGHT_BLUE: true;
    MFT_OFF_WHITE: true;
    MFT_CREAM: true;
    MFT_GREEN: true;
    NICE_GREEN: true;
    NICE_ORANGE: true;
  }
}

declare module "@mui/material/SvgIcon" {
  export interface SvgIconPropsColorOverrides {
    MFT_ORANGE: true;
    MFT_DARK_BLUE: true;
    MFT_LIGHT_BLUE: true;
    MFT_OFF_WHITE: true;
    MFT_CREAM: true;
    MFT_GREEN: true;
    NICE_GREEN: true;
    NICE_ORANGE: true;
  }
}

export const MFT_COLORS = {
  MFT_ORANGE: "#FF8C3D",
  MFT_DARK_BLUE: "#093952",
  MFT_LIGHT_BLUE: "#BCE1FF",
  MFT_OFF_WHITE: "#F1F6F8",
  MFT_CREAM: "#DFD0BF",
  MFT_GREEN: "#8ACEBA",
  NICE_GREEN: "#66D575",
  NICE_ORANGE: "#FEC843",
};

export const lightTheme = createTheme({
  components: {
    MuiTypography: {
      defaultProps: {
        component: "span",
        color: "textPrimary",
      },
    },
  },
  typography: {
    h6: {
      fontFamily: "Europa",
    },
    h5: {
      fontFamily: "Europa",
    },
  },
  palette: {
    mode: "light",
    MFT_ORANGE: "#FF8C3D",
    MFT_DARK_BLUE: "#093952",
    MFT_LIGHT_BLUE: "#BCE1FF",
    MFT_OFF_WHITE: "#F1F6F8",
    MFT_CREAM: "#DFD0BF",
    MFT_GREEN: "#8ACEBA",
    NICE_GREEN: "#66D575",
    NICE_ORANGE: "#FEC843",
    primary: {
      main: "#093952",
    },
    secondary: {
      main: "#FF8C3D",
    },
    success: {
      main: "#8aceba",
    },
    info: {
      main: "#bce1ff",
    },
    background: {
      default: "#f1f6f8",
    },
  },
});

export const darkTheme = createTheme({
  components: {
    MuiTypography: {
      defaultProps: {
        component: "span",
        color: "textPrimary",
      },
    },
    MuiTextField: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiInput: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiFilledInput: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(255, 255, 255, 0.06)",
        },
      },
    },
  },
  typography: {
    h6: {
      fontFamily: "Europa",
    },
    h5: {
      fontFamily: "Europa",
    },
  },
  palette: {
    mode: "dark",
    MFT_ORANGE: "#FF8C3D",
    MFT_DARK_BLUE: "#093952",
    MFT_LIGHT_BLUE: "#BCE1FF",
    MFT_OFF_WHITE: "#F1F6F8",
    MFT_CREAM: "#DFD0BF",
    MFT_GREEN: "#8ACEBA",
    NICE_GREEN: "#66D575",
    NICE_ORANGE: "#FEC843",
    primary: {
      main: "#58a6d4",
    },
    secondary: {
      main: "#ff8c3d",
    },
    success: {
      main: "#8aceba",
    },
    info: {
      main: "#bce1ff",
    },
    background: {
      default: "#0d1117",
      paper: "#161b22",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
});

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return context;
};

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode === "dark" || savedMode === "light" ? savedMode : "light";
  });

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = mode === "dark" ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export enum Colors {
  MFT_ORANGE = "#FF8C3D",
  MFT_DARK_BLUE = "#093952",
  MFT_LIGHT_BLUE = "#BCE1FF",
  MFT_OFF_WHITE = "#F1F6F8",
  MFT_CREAM = "#DFD0BF",
  MFT_GREEN = "#8ACEBA",
  NICE_GREEN = "#66D575",
  NICE_ORANGE = "#FEC843",
}
